import { Injectable, ErrorHandler } from '@angular/core';
import {
	HttpRequest,
	HttpHandler,
	HttpInterceptor,
	HttpHeaderResponse,
	HttpSentEvent,
	HttpProgressEvent,
	HttpResponse,
	HttpUserEvent,
	HttpEvent
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { UserActiveService } from './user-active.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '../view-shared/dialogs/dialog.service';
import { ToastService } from '../view-shared/dialogs/toast.service';
import { Router } from '@angular/router';

/* HTTP response status codes

Informational responses (100–199)
Successful responses (200–299)
Redirects (300–399)
Client errors (400–499)
Server errors (500–599)
*/

export const criticalRoutes: string[] = [
	'api/installationinit',
	'api/ParameterGroups/1',
	'api/Units',
	'api/Parameters',
	'api/points/annotations',
	'api/batchinit',
	'api/events',
	'api/SensorHubs/Health',
	'api/Organizations',
	'api/Users/GetAllUsers',
	'api/UserGroups/GetAllGroups',
	'api/sensormanagerinit'
];

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
	constructor(
		private translate: TranslateService,
		private dialogService: DialogService
	) {}

	handleError(error: any): void {
		console.warn('GlobalErrorHandler caught an error:', error);

		/** Handles chunk load errors (module not found) due to cache issues */
		const chunkFailedMessage = /Loading chunk [\d]+ failed/;
		if (chunkFailedMessage.test(error.message)) {
			const handle = () => {
				if ('caches' in window) {
					caches.keys().then(names => {
						for (let name of names) {
							caches.delete(name);
						}
					});
				}
				window.location.reload();
			};

			this.dialogService.showAlert(
				this.translate.instant(
					'ROOT.ERRORS.HTTP.OLD_VERSION_DETECTED.TITLE'
				),
				this.translate.instant(
					'ROOT.ERRORS.HTTP.OLD_VERSION_DETECTED.CONTENT'
				),
				'default',
				handle
			);
		}
	}
}

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
	constructor(
		private translate: TranslateService,
		private dialogService: DialogService,
		private toastService: ToastService,
		private authService: UserActiveService,
		private router: Router
	) {}
	tokenSubject: BehaviorSubject<string> = new BehaviorSubject(null);

	/**
	 * Intercepts HTTP requests to add an authorization token and handle errors.
	 *
	 * @param request - The outgoing HTTP request.
	 * @param next - The next HTTP handler in the chain.
	 * @returns An observable of the HTTP event or error handling.
	 */
	intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<
		| HttpSentEvent
		| HttpHeaderResponse
		| HttpProgressEvent
		| HttpResponse<any>
		| HttpUserEvent<any>
		| HttpEvent<any>
		| any
	> {
		// Handle the request by adding the authorization token
		return next
			.handle(
				this.addTokenToRequest(request, this.authService.getAuthToken())
			)
			.pipe(
				// Catch any errors that occur during the HTTP request
				catchError(err => {
					console.log('interceptor caught an error:', err);

					// If the error status is 401 (Unauthorized) or 405 (Method Not Allowed)
					if ([401, 405].includes(err.status)) {
						// The token died, display message and eject to login page
						const handle = () => {
							this.authService.deAuthUser();
							if (location.href.indexOf('/outside') >= 0) {
								location.reload();
							} else {
								this.router.navigate(['/outside']);
							}
						};

						this.dialogService.showAlert(
							this.translate.instant(
								'ROOT.ERRORS.HTTP.TOKEN_EXPIRED.CONTENT'
							),
							this.translate.instant(
								'ROOT.ERRORS.HTTP.TOKEN_EXPIRED.TITLE'
							),
							'default',
							handle
						);
						return throwError(err);
						// return this.handle401Error(request, next);
					}

					// If the error status is 400 (Bad Request) and the error indicates an invalid login attempt
					if (
						err.status === 400 &&
						err.error &&
						err.error === 'INVALID_LOGIN_ATTEMPT'
					) {
						// Show a warning toast notification
						this.toastService.showToast(
							this.translate.instant(
								'ROOT.ERRORS.HTTP.INVALID_LOGIN_ATTEMPT'
							),
							'warning'
						);

						// Throw the error to be handled by the caller
						return throwError(err);
					}

					// Determine if the error has additional information to display
					const additionalInfo = err.error.Message
						? err.error.Message
						: err.message
						? err.message
						: null;

					// If the error is from a critical route, show an alert and force the user to login
					if (criticalRoutes.some(route => err.url.includes(route))) {
						const handle = () => {
							this.authService.deAuthUser();
							if (location.href.indexOf('/outside') >= 0) {
								location.reload();
							} else {
								this.router.navigate(['/outside']);
							}
						};

						this.dialogService.showAlert(
							this.translate.instant(
								'ROOT.ERRORS.HTTP.REQUEST_ERROR.CONTENT'
							),
							this.translate.instant(
								'ROOT.ERRORS.HTTP.API_INIT_FAILED'
							),
							'error',
							handle
						);
					}
					// If the error is not from a critical route, show a warning toast notification
					else {
						const requestUrl = err?.url || '';
						const regexRequestUrlForTimeseriesPointParam = /\/api\/timeseries\/point\/\d+\/parameter\//;
						const isTimeseriesPointParamUrl = regexRequestUrlForTimeseriesPointParam.test(
							requestUrl
						);
						const isNotFoundError = err.status === 404;
						const hideToastForTimeSeriesPointParamUrl =
							isTimeseriesPointParamUrl && isNotFoundError;
						const isLoginBadRequest =
							(err.status === 400 || err.status === 423) &&
							requestUrl.includes('/Account/Login');

						if (
							!hideToastForTimeSeriesPointParamUrl &&
							!isLoginBadRequest
						) {
							const handle = (confirmed: boolean) => {
								this.dialogService.showAlert(
									this.translate.instant(
										'ROOT.ERRORS.HTTP.REQUEST_ERROR.CONTENT'
									),
									additionalInfo
										? additionalInfo
										: 'No error message found',
									'warning'
								);
							};
							this.toastService.showToast(
								this.translate.instant(
									'ROOT.ERRORS.HTTP.REQUEST_ERROR.TITLE'
								),
								'warning',
								handle
							);
						}
					}

					// Throw the error to be handled by the caller
					return throwError(err);
				})
			);
	}

	/**
	 * Adds an authorization token to the HTTP request headers.
	 * Optionally adds a header to bypass the service worker cache for non-asset requests.
	 *
	 * @param request - The original HTTP request.
	 * @param token - The authorization token to be added to the request headers.
	 * @returns The cloned HTTP request with the new headers.
	 */
	private addTokenToRequest(
		request: HttpRequest<any>,
		token: string
	): HttpRequest<any> {
		// Create headers object with the Authorization token
		const headers = { Authorization: `Bearer ${token}` };

		// Bypass service worker cache if the request is not for an asset
		if (!request.url.includes('/assets/')) {
			headers['ngsw-bypass'] = '';
		}

		// Clone the original request and attach the new headers
		const req = request.clone({
			setHeaders: headers
		});

		// Return the modified request
		return req;
	}

	/**
	 * Handles HTTP 401 Unauthorized errors by attempting to refresh the token.
	 * If token refresh is successful, retries the original request with the new token.
	 * If token refresh fails, de-authenticates the user and reloads the page.
	 *
	 * @param request - The original HTTP request that resulted in a 401 error.
	 * @param next - The next HTTP handler in the chain.
	 * @returns An observable that either retries the original request or handles the error.
	 */
	private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
		// Set the current token subject to null indicating an invalid or expired token
		this.tokenSubject.next(null);

		// Call the authService to refresh the token
		return this.authService.refreshToken().pipe(
			// On successful token refresh
			switchMap((token: string) => {
				if (token) {
					// Update the token subject with the new token
					this.tokenSubject.next(token);
					// Retry the failed request with the new token
					return next.handle(this.addTokenToRequest(request, token));
				} else {
					// If token refresh failed, de-authenticate the user and reload the page
					this.authService.deAuthUser();
					location.reload();
					// Return an observable of false to indicate failure
					return of(false);
				}
			}),
			// Catch any errors that occur during the token refresh process
			catchError(err => {
				// De-authenticate the user and reload the page on error
				this.authService.deAuthUser();
				location.reload();
				// Return an observable of false to indicate failure
				return of(false);
			})
		);
	}
}
